import React from 'react';
import { Link, NavLink as RouterNavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { LogOut } from 'lucide-react';
import { Separator } from '../ui/separator';
import { useAccessButton } from './useAccessButton';
import UserDropdown from './UserDropdown';
import NavLinks from './NavLinks';
import ToolsDropdown from './ToolsDropdown';
import MobileNav from './MobileNav';
import AccessButtonMount from './AccessButtonMount';

interface NavigationBarProps {
  hideAccessButton?: boolean;
  isNativeTab?: boolean;
}

const NavigationBar: React.FC<NavigationBarProps> = ({ hideAccessButton = false, isNativeTab = false }) => {
  const auth = useAuth();
  const { accessButtonDisabled } = useAccessButton(auth);

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-border/40 bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
        <div className="container flex h-14 items-center">
          {/* Logo */}
          <Link to="/" className="mr-10 flex items-center gap-2.5 group">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm shadow-sm ring-1 ring-primary/20 transition-all duration-200 group-hover:scale-105 group-hover:shadow-md group-hover:ring-primary/40">
              N
            </div>
            <span className="font-semibold text-[15px] tracking-tight hidden sm:inline-block transition-colors duration-200 group-hover:text-primary">
              Narcis EvoApp
            </span>
          </Link>

          {/* Desktop nav */}
          <NavLinks />
          <Separator orientation="vertical" className="hidden lg:block h-5 mx-2" />
          <ToolsDropdown />

          {/* Spacer */}
          <div className="ml-auto" />

          {/* Desktop right side */}
          {!isNativeTab && (
            <div className="hidden lg:flex items-center">
              {auth.isAuthenticated ? (
                <UserDropdown />
              ) : (
                <RouterNavLink
                  to="/identity"
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md transition-colors'
                  )}
                >
                  <LogOut className="h-4 w-4" />
                  Login
                </RouterNavLink>
              )}
            </div>
          )}

          {/* Mobile menu */}
          <MobileNav isNativeTab={isNativeTab} />
        </div>
      </header>

      {!hideAccessButton && <AccessButtonMount accessButtonDisabled={accessButtonDisabled} />}
    </>
  );
};

export default NavigationBar;
